import { createBrowserRouter, redirect } from 'react-router-dom';
import HomePage from '../pages/HomePage.jsx';
import AuthPage from '../pages/AuthPage.jsx';
import NotFoundPage from '../pages/NotFoundPage.jsx';
import App from '@/App.jsx';
import ChatPage from '@/pages/ChatPage.jsx';
import { getAdminTokens, getTokens } from '@/network/project.service.js';
import { useUserStore } from '@/stores/useUser.store.js';
import ProjectPage from '@/pages/ProjectPage.jsx';
import PermitProgressStepper from '@/pages/PermitProgressStepper.jsx';
import ProjectHome from '@/pages/ProjectHome.jsx';
import { useProjectStore } from '@/stores/useProject.store.js';
import { appAxios } from '@/network/base/appAxios.js';
import { getGuestId } from '@/utils/getGuestId.jsx';
import AdminAuthPage from '@/pages/AdminAuthPage.jsx';
import AdminDashboardLayout from '@/layouts/AdminDashboardLayout.jsx';
import Dashboard from '@/pages/admin/Dashboard.jsx';
import Requests from '@/pages/admin/Requests.jsx';
import Projects from '@/pages/admin/Projects.jsx';
import AutomationFailures from '@/pages/admin/AutomationFailures.jsx';
import Analytics from '@/pages/admin/Analytics.jsx';
import Team from '@/pages/admin/Team.jsx';
import AdminSettings from '@/pages/admin/Settings.jsx';
import PermitDetail from '@/pages/admin/PermitDetail.jsx';
import AddAdmin from '@/pages/admin/AddAdmin.jsx';
import ReviewPermits from '@/pages/admin/ReviewPermits.jsx';
import PermitProjectDetail from '@/components/PermitProjectDetail.jsx';
import TermsAndPolicies from '@/pages/TermsAndPolicies.jsx';
import IpManagement from '@/pages/admin/IpManagement.jsx';
import SubscriptionPage from '@/pages/SubscriptionPage.jsx';
import CredsManagement from '@/pages/admin/CredsManagement.jsx';
import GuestChats from '@/pages/admin/GuestChats.jsx';
const { setAuth, setAdminDetails, setUserRole } = useUserStore.getState();

const preloadProjectData = async (retryCount = 0) => {
  const { getProjects, getPermitChatGroups, projects, permitChatGroups } = useProjectStore.getState();

  await Promise.all([getProjects(), getPermitChatGroups()]);

  // After loading, check if data exists - if not, retry up to 2 times with delay
  const state = useProjectStore.getState();
  const hasData = state.projects?.length > 0 || state.permitChatGroups?.length > 0;

  if (!hasData && retryCount < 2) {
    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
    return preloadProjectData(retryCount + 1);
  }

  return useProjectStore.getState();
};

const getUserRole = async () => {
  try {
    const response = await appAxios.get('/Authorization/v1.0/getUserRole');
    const role = response.data.data;
    setUserRole(role);
    return role;
  } catch (error) {
    console.error('Failed to get user role:', error);
    return null;
  }
};

// Auth loader function
const authLoader = async ({ request }) => {
  const Authorization = localStorage.getItem('Authorization');
  const RefreshToken = localStorage.getItem('RefreshToken');

  // Use request.url (the navigation TARGET url) — window.location is stale during loader execution
  const targetUrl = new URL(request.url);
  const priceId = targetUrl.searchParams.get('price_id');
  const from_zermit_bool = targetUrl.searchParams.get('from_zermit') === 'true';
  const authcode = targetUrl.searchParams.get('authcode');
  const isSubscriptionRoute = targetUrl.pathname.endsWith('/citypermit/subscription');

  if (Authorization && RefreshToken) {
    setAuth(true);
    await preloadProjectData();
    // Redirect to subscription only when not already navigating there, to prevent a redirect loop.
    // authLoader runs for ALL /citypermit/* children including /citypermit/subscription itself.
    if (priceId && from_zermit_bool && !isSubscriptionRoute) {
      return redirect(`/citypermit/subscription?price_id=${priceId}&from_zermit=true`);
    }
    return null;
  }

  if (authcode && !Authorization && !RefreshToken) {
    const { data } = await getTokens({ authcode });
    localStorage.setItem('Authorization', data.data.accessToken);
    localStorage.setItem('RefreshToken', data.data.refreshToken);
    setAuth(true);
    await appAxios.post('/User/v1.0/migration', { guestId: getGuestId() });
    await getUserRole();
    await preloadProjectData();
    if (priceId && from_zermit_bool) {
      return redirect(`/citypermit/subscription?price_id=${priceId}&from_zermit=true`);
    }
    return redirect('/citypermit/project/new-chat');
  }

  if (!Authorization) {
    if (priceId && from_zermit_bool) {
      return redirect(`/auth?price_id=${priceId}&from_zermit=true`);
    }
    return redirect('/auth');
  }

  return null;
};

// Admin Auth loader function
const adminAuthLoader = async () => {
  const Authorization = localStorage.getItem('Authorization');
  const RefreshToken = localStorage.getItem('RefreshToken');
  const params = new URLSearchParams(window.location.search);
  const authcode = params.get('authcode');

  if (Authorization && RefreshToken) {
    setAuth(true);
    // Fetch admin details if not already loaded
    const { adminDetails } = useUserStore.getState();
    if (!adminDetails) {
      try {
        const { data } = await appAxios.get('/Admin/v1.0/admin-details');
        setAdminDetails(data.data);
      } catch (error) {
        console.error('Failed to fetch admin details:', error);
      }
    }
    return null;
  }

  if (authcode && !Authorization && !RefreshToken) {
    const { data } = await getAdminTokens({ authcode });
    localStorage.setItem('Authorization', data.data.accessToken);
    localStorage.setItem('RefreshToken', data.data.refreshToken);
    setAuth(true);
    try {
      const adminData = await appAxios.get('/Admin/v1.0/admin-details');
      setAdminDetails(adminData.data.data);
    } catch (error) {
      console.error('Failed to fetch admin details:', error);
    }
    return redirect('/admin/dashboard');
  }
  if (!Authorization) {
    return redirect('/admin/auth');
  }
  return null;
};

// Public loader function (redirect if already authenticated)
const publicLoader = () => {
  const token = localStorage.getItem('Authorization');
  if (token) {
    return redirect('/citypermit/project/new-chat');
  }
  return null;
};

const adminPublicLoader = () => {
  const token = localStorage.getItem('Authorization');
  if (token) {
    return redirect('/admin/dashboard');
  }
  return null;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    loader: publicLoader,
    children: [
      {
        path: '/',
        index: true,
        element: <ChatPage />,
      },
      {
        path: 'subscription',
        element: <SubscriptionPage protectedRoute={false} />,
      },
    ],
  },
  {
    path: '/terms',
    element: <App />,
    children: [
      {
        index: true,
        element: <TermsAndPolicies />,
      },
    ],
  },
  {
    path: '/help',
    element: <App />,
    children: [
      {
        index: true,
        element: <TermsAndPolicies />,
      },
    ],
  },
  // Protected routes
  {
    path: '/citypermit',
    element: <App />,
    loader: authLoader,
    children: [
      {
        path: 'chat',
        index: true,
        element: <ChatPage protectedRoute={true} />,
      },
      {
        path: 'project',
        element: <ProjectPage />,
        children: [
          {
            index: true,
            element: <ProjectHome />,
          },
          {
            path: 'progress/:projectId',
            element: <PermitProgressStepper />,
          },
          {
            path: 'chat/:permitChatId',
            element: <ChatPage protectedRoute={true} />,
          },
          {
            path: 'new-chat',
            element: <ChatPage protectedRoute={true} />,
          },
          {
            path: 'chat/:permitChatId/permit/:permitId',
            element: <PermitProjectDetail />,
          },
        ],
      },
      {
        path: 'subscription',
        element: <SubscriptionPage protectedRoute={true} />,
      },
      // {
      //   path: 'project',
      //   index: true,
      //   element: <ProjectPage />,
      // },
      // // for Demo
      // {
      //   path: 'progress/:permitId',
      //   index: true,
      //   element: <PermitProgressStepper />,
      // },
    ],
  },
  {
    path: '/auth',
    loader: publicLoader, // Only redirect authenticated users AWAY from login
    element: <AuthPage />,
  },
  {
    path: '/admin/auth',
    loader: adminPublicLoader, // Only redirect authenticated users AWAY from login
    element: <AdminAuthPage />,
  },
  // Admin Dashboard Routes with Outlet structure
  {
    path: '/admin/dashboard',
    loader: adminAuthLoader,
    element: <AdminDashboardLayout />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      {
        path: 'requests',
        element: <Requests />,
      },
      {
        path: 'permits/:permitId',
        element: <PermitDetail />,
      },
      // {
      //   path: 'projects',
      //   element: <Projects />,
      // },
      {
        path: 'creds-management',
        element: <CredsManagement />,
      },
      {
        path: 'guest-chat',
        element: <GuestChats />,
      },
      {
        path: 'projects',
        element: <Projects />,
      },
      // {
      //   path: 'new-requests',
      //   element: <AutomationFailures />,
      // },
      {
        path: 'automation-failures',
        element: <AutomationFailures />,
      },
      {
        path: 'review-permits',
        element: <ReviewPermits />,
      },
      {
        path: 'analytics',
        element: <Analytics />,
      },
      {
        path: 'team',
        element: <Team />,
      },
      {
        path: 'settings',
        element: <AdminSettings />,
      },
      {
        path: 'add-admin',
        element: <AddAdmin />,
      },
      {
        path: 'ip-management',
        element: <IpManagement />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);

export default router;
